import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Repeat, BellRing, Volume2, Vibrate, LockKeyhole, AlarmClockOff } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { ScrollReveal } from '../../components/ScrollReveal';
import { RoutineLiveDemo } from '../../components/RoutineLiveDemo';

const dataModel = [
  { icon: Clock, title: 'Horário próprio', desc: 'Cada subrotina tem seu próprio horário, como "06:30" ou "07:00".' },
  { icon: Calendar, title: 'Dias da semana', desc: 'Pode valer só de segunda a sexta, só nos fins de semana, ou todo dia.' },
  { icon: Repeat, title: 'Repetição automática', desc: 'Uma vez configurada, a subrotina repete sozinha nos dias certos.' },
  { icon: BellRing, title: 'Alarme opcional', desc: 'Qualquer etapa pode virar um alarme de verdade, não só um lembrete visual.' },
];

const RotinasPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="relative pt-16 pb-14 md:pt-20 md:pb-16 bg-background overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.35] dark:opacity-[0.12]"
          style={{ backgroundImage: 'radial-gradient(rgb(var(--primary)) 1px, transparent 1px)', backgroundSize: '26px 26px' }}
          aria-hidden="true"
        />
        <div
          className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-25 blur-3xl pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgb(var(--primary)) 0%, transparent 70%)' }}
          aria-hidden="true"
        />

        <div className="relative container mx-auto px-4">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors mb-6">
            <ArrowLeft className="w-4 h-4" />
            Início
          </Link>

          <div className="inline-flex items-center gap-2 bg-primary/10 dark:bg-primary/15 px-3 py-1.5 rounded-full text-sm font-semibold text-foreground mb-5">
            <Calendar className="w-4 h-4 text-primary-ink dark:text-primary-tint" />
            Rotinas e Alarmes
          </div>

          <h1 className="text-4xl md:text-6xl font-bold text-foreground max-w-3xl leading-[1.1]">
            Organize o dia com <span className="text-primary-ink dark:text-primary-tint">previsibilidade</span>
          </h1>
          <p className="font-body text-lg md:text-xl text-muted-foreground mt-4 max-w-2xl">
            Checklists visuais e alarmes reais para cada etapa da rotina — pra reduzir a
            ansiedade do "o que vem agora".
          </p>
        </div>
      </section>

      {/* Demo ao vivo + estrutura de dados, lado a lado */}
      <ScrollReveal>
        <section className="py-16 md:py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
              <div className="w-full max-w-sm mx-auto lg:order-2">
                <RoutineLiveDemo />
              </div>

              <div className="lg:order-1">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
                  Cada subrotina, com horário e repetição
                </h2>
                <div className="space-y-4">
                  {dataModel.map((item) => (
                    <div key={item.title} className="flex items-start gap-3">
                      <span className="w-9 h-9 rounded-lg bg-background flex items-center justify-center flex-shrink-0 shadow-sm">
                        <item.icon className="w-4 h-4 text-primary-ink dark:text-primary-tint" />
                      </span>
                      <div>
                        <h3 className="font-semibold text-foreground text-sm">{item.title}</h3>
                        <p className="text-sm text-muted-foreground mt-0.5">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* Alarme real — mock ilustrativo, sem imagem */}
      <ScrollReveal>
        <section className="py-16 md:py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                  Um alarme de verdade, não só um lembrete
                </h2>
                <p className="text-base text-muted-foreground leading-relaxed mb-4">
                  Quando uma subrotina é marcada como alarme, ela dispara uma tela cheia sobre
                  a tela de bloqueio — com som, vibração e um botão de soneca — pensada pra
                  funcionar mesmo se o app estiver fechado.
                </p>
              </div>

              <div className="rounded-[2rem] bg-[#121212] p-6 text-white">
                <div className="flex items-center justify-between text-xs text-white/60 mb-6">
                  <span className="flex items-center gap-1.5">
                    <LockKeyhole className="w-3.5 h-3.5" />
                    Tela bloqueada
                  </span>
                  <span>07:00</span>
                </div>
                <div className="flex flex-col items-center text-center gap-3 py-4">
                  <span className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                    <BellRing className="w-7 h-7 text-primary-tint" />
                  </span>
                  <h3 className="font-bold text-lg">Vestir a roupa</h3>
                  <div className="flex items-center gap-4 text-white/60 text-xs mt-1">
                    <span className="flex items-center gap-1">
                      <Volume2 className="w-3.5 h-3.5" />
                      Som
                    </span>
                    <span className="flex items-center gap-1">
                      <Vibrate className="w-3.5 h-3.5" />
                      Vibração
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-4">
                  <span className="flex-1 flex items-center justify-center gap-1.5 rounded-full bg-white/10 py-2.5 text-xs font-semibold">
                    <AlarmClockOff className="w-3.5 h-3.5" />
                    Soneca
                  </span>
                  <span className="flex-1 flex items-center justify-center rounded-full bg-primary py-2.5 text-xs font-semibold">
                    Parar
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </ScrollReveal>

      {/* CTA final */}
      <section className="py-16 bg-primary/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
            Quer ver os outros recursos?
          </h2>
          <Link
            to="/recursos/jogos"
            className="inline-flex items-center gap-1.5 font-sans font-bold text-[15px] px-7 py-3.5 rounded-full bg-primary text-white hover:bg-primary/90 transition-transform hover:-translate-y-0.5"
          >
            Conhecer os Jogos Sociais
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RotinasPage;
